import PropTypes from 'prop-types';
import React from 'react';
import { BasePage, Helmet, FiveHundredErrorPage } from '@sm/webassets';
import { Grid, Row, Col } from '@sm/wds-react';

import MainHeading from '~pricing/components/MainHeading';
import PricingNavigation from '~pricing/components/PricingNavigation';
import { Package, SummaryFooter } from '~pricing/components/Summary';
import usePricingExperience from '~pricing/lib/PricingExperience';
import { PricingExperienceProvider } from '~pricing/lib/PricingExperience/PricingExperienceContext';
import { INDIVIDUAL_PACKAGES, TEAMS_PACKAGES } from '~pricing/lib/constants';
import { PackagePropTypes } from '~pricing/lib/proptypes';

const createPackagesList = (packages, packageSet) => {
  // The provided package list is both the filter and determines the order
  return packageSet
    .map(packageName => packages.find(packageObj => packageObj.package === packageName))
    .filter(packageObj => {
      // If pricingsvc didn't return all the packages we expected, carry on
      return !!packageObj;
    });
};

const PricingSummaryPage = ({ packageData, packageSet, packageTypes }) => {
  const displayedPackages = createPackagesList(packageData, packageSet);

  return (
    <BasePage pageId="Pricing Summary" legacyWeb="pricingweb" sticky={false}>
      <Helmet>
        <base target="_top" href="/" />
        <title>SurveyMonkey Plans and Pricing</title>
        <meta
          name="keywords"
          content="questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys"
        />
        <meta
          name="description"
          content="Create and publish online surveys in minutes, and view results graphically and in real time. SurveyMonkey provides free online questionnaire and survey software."
        />
      </Helmet>
      <Grid>
        <Row>
          <Col>
            <main>
              <header>
                <MainHeading />
                <PricingNavigation packageType={packageTypes} />
              </header>
              <div className="packages wds-flex wds-flex--x-center wds-flex--stretch" key="packageList">
                {displayedPackages.map(pricingPackage => {
                  return <Package {...pricingPackage} key={`package-${pricingPackage.package}`} />;
                })}
              </div>
              <SummaryFooter packages={packageData} />
            </main>
          </Col>
        </Row>
      </Grid>
    </BasePage>
  );
};

PricingSummaryPage.propTypes = {
  packageData: PropTypes.arrayOf(PropTypes.shape(PackagePropTypes)).isRequired,
  packageSet: PropTypes.arrayOf(PropTypes.string).isRequired,
  packageTypes: PropTypes.string.isRequired,
};

const PricingSummary = ({
  match: {
    params: { packageTypes = 'teams' },
  },
}) => {
  const packageSet = packageTypes === 'individual' ? INDIVIDUAL_PACKAGES : TEAMS_PACKAGES;
  const { error, loading, data } = usePricingExperience();

  return (
    <Choose>
      {/* if there was an issue loading the data from pricingsvc, we've got nothing to show. bail */}
      <When condition={error}>
        <FiveHundredErrorPage />
      </When>
      <When condition={!error && !loading && data}>
        <PricingExperienceProvider value={data.pricingPageExperience.package}>
          <PricingSummaryPage
            packageData={data.pricingPageExperience.package}
            packageSet={packageSet}
            packageTypes={packageTypes}
          />
        </PricingExperienceProvider>
      </When>
    </Choose>
  );
};

PricingSummary.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      packageTypes: PropTypes.string,
    }),
  }),
};

PricingSummary.defaultProps = {
  match: {
    params: {
      packageTypes: 'teams',
    },
  },
};

export default PricingSummary;
